import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  FileText, Plus, MoreHorizontal, Search, Pencil, Eye, History,
  ToggleLeft, ToggleRight, Trash2, Copy, Check, X, AlertTriangle,
  ChevronRight, ChevronDown, Variable, Clock, User, ArrowLeft,
  GripVertical, RotateCcw, Archive,
} from "lucide-react";
import { toast } from "sonner";
import { DocumentTemplatesService } from "@/services/document-templates.service";
import {
  DocumentTemplate, TemplateCategory, TemplateStatus, TemplateSection,
  TemplateVersion, TEMPLATE_VARIABLES,
  resolveVariables, extractVariables, validateTemplate,
  getCurrentSections, getExampleValues,
} from "@/data/documentTemplateEngine";

const CATEGORY_STYLES: Record<TemplateCategory, string> = {
  Contract: "bg-primary/10 text-primary border-primary/20",
  Disclosure: "bg-warning/10 text-warning border-warning/20",
  "Change Order": "bg-info/10 text-info border-info/20",
  Form: "bg-accent/10 text-accent-foreground border-accent/20",
};

const STATUS_COLORS: Record<TemplateStatus, string> = {
  Active: "bg-success/10 text-success border-success/20",
  Inactive: "bg-muted text-muted-foreground border-border",
  Archived: "bg-muted text-muted-foreground border-border",
};

const SOURCE_COLORS: Record<string, string> = {
  customer: "bg-info/10 text-info",
  quote: "bg-primary/10 text-primary",
  financial: "bg-success/10 text-success",
  system: "bg-muted text-muted-foreground",
  job: "bg-warning/10 text-warning",
};

export function DocumentFormEngine() {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  useEffect(() => {
    DocumentTemplatesService.getAll()
      .then(setTemplates)
      .catch(() => {/* table not yet created — stay empty */})
      .finally(() => setLoadingTemplates(false));
  }, []);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Editor state
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [editorSections, setEditorSections] = useState<TemplateSection[]>([]);
  const [editorTab, setEditorTab] = useState<"edit" | "preview" | "variables" | "history">("edit");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [changeNotes, setChangeNotes] = useState("");

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({ name: "", category: "Contract" as TemplateCategory });

  // Version history dialog
  const [versionDialog, setVersionDialog] = useState<DocumentTemplate | null>(null);

  const filtered = useMemo(() => {
    return templates.filter(t => {
      if (filterCategory !== "all" && t.category !== filterCategory) return false;
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [templates, search, filterCategory, filterStatus]);

  const stats = useMemo(() => ({
    total: templates.length,
    active: templates.filter(t => t.status === "Active").length,
    contracts: templates.filter(t => t.category === "Contract").length,
    forms: templates.filter(t => t.category === "Form").length,
  }), [templates]);

  /* ── Template CRUD ── */
  const handleCreate = () => {
    if (!createDraft.name.trim()) return;
    const defaultSections: TemplateSection[] = [
      { id: `s-${Date.now()}-1`, title: "Header", type: "header", content: `{{company_name}}\n{{company_address}} · {{company_phone}}\n\nDate: {{date}}`, order: 1 },
      { id: `s-${Date.now()}-2`, title: "Content", type: "body", content: "Customer: {{customer_name}}\nAddress: {{address}}\n\n[Add your content here]", order: 2 },
    ];
    if (["Contract", "Change Order", "Disclosure"].includes(createDraft.category)) {
      defaultSections.push(
        { id: `s-${Date.now()}-3`, title: "Terms & Conditions", type: "terms", content: "[Add terms and conditions]", order: 3 },
        { id: `s-${Date.now()}-4`, title: "Signatures", type: "signature", content: "Homeowner: ______________________ Date: ______\nRepresentative: {{sales_rep_name}} Date: {{date}}", order: 4 },
      );
    }
    const newTemplate: DocumentTemplate = {
      id: `tpl-${Date.now()}`,
      name: createDraft.name,
      category: createDraft.category,
      status: "Active",
      currentVersion: 1,
      versions: [{
        version: 1,
        sections: defaultSections,
        createdAt: new Date().toISOString().slice(0, 10),
        createdBy: "Current User",
        changeNotes: "Initial version",
      }],
      createdAt: new Date().toISOString().slice(0, 10),
      createdBy: "Current User",
      lastUpdatedAt: new Date().toISOString().slice(0, 10),
      lastUpdatedBy: "Current User",
    };
    DocumentTemplatesService.create(newTemplate).catch(() => {});
    setTemplates(prev => [...prev, newTemplate]);
    setCreateOpen(false);
    setCreateDraft({ name: "", category: "Contract" });
    toast.success("Template created");
    openEditor(newTemplate);
  };

  const handleDuplicate = (t: DocumentTemplate) => {
    const dup: DocumentTemplate = {
      ...t,
      id: `tpl-${Date.now()}`,
      name: `${t.name} (Copy)`,
      currentVersion: 1,
      versions: [{ ...t.versions[t.versions.length - 1], version: 1, createdAt: new Date().toISOString().slice(0, 10), createdBy: "Current User", changeNotes: `Duplicated from ${t.name}` }],
      createdAt: new Date().toISOString().slice(0, 10),
      createdBy: "Current User",
      lastUpdatedAt: new Date().toISOString().slice(0, 10),
      lastUpdatedBy: "Current User",
    };
    DocumentTemplatesService.create(dup).catch(() => {});
    setTemplates(prev => [...prev, dup]);
    toast.success("Template duplicated");
  };

  const handleToggleStatus = (id: string) => {
    setTemplates(prev => prev.map(t => {
      if (t.id !== id) return t;
      const newStatus = t.status === "Active" ? "Inactive" : "Active";
      DocumentTemplatesService.update(id, { status: newStatus }).catch(() => {});
      return { ...t, status: newStatus as TemplateStatus, lastUpdatedAt: new Date().toISOString().slice(0, 10), lastUpdatedBy: "Current User" };
    }));
    toast.success("Template status updated");
  };

  const handleArchive = (id: string) => {
    DocumentTemplatesService.update(id, { status: "Archived" }).catch(() => {});
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, status: "Archived" as TemplateStatus, lastUpdatedAt: new Date().toISOString().slice(0, 10), lastUpdatedBy: "Current User" } : t));
    toast.success("Template archived");
  };

  const handleDelete = (id: string) => {
    DocumentTemplatesService.delete(id).catch(() => {});
    setTemplates(prev => prev.filter(t => t.id !== id));
    toast.success("Template deleted");
  };

  /* ── Editor ── */
  const openEditor = (t: DocumentTemplate) => {
    const sections = getCurrentSections(t);
    setEditorSections(sections.map(s => ({ ...s })));
    setEditingTemplate(t);
    setEditorTab("edit");
    setChangeNotes("");
    setEditingSectionId(null);
  };

  const handleSaveTemplate = () => {
    if (!editingTemplate) return;
    const validation = validateTemplate(editorSections);
    if (!validation.valid) {
      toast.warning(`Template has issues: ${validation.issues.join(", ")}`);
    }
    const newVersion = editingTemplate.currentVersion + 1;
    const newVersionEntry: TemplateVersion = {
      version: newVersion,
      sections: editorSections.map(s => ({ ...s })),
      createdAt: new Date().toISOString().slice(0, 10),
      createdBy: "Current User",
      changeNotes: changeNotes || "Updated template",
    };
    const updatedVersions = [...editingTemplate.versions, newVersionEntry];
    DocumentTemplatesService.update(editingTemplate.id, {
      currentVersion: newVersion,
      versions: updatedVersions,
      lastUpdatedBy: "Current User",
    }).catch(() => {});
    setTemplates(prev => prev.map(t => {
      if (t.id !== editingTemplate.id) return t;
      return {
        ...t,
        currentVersion: newVersion,
        versions: updatedVersions,
        lastUpdatedAt: new Date().toISOString().slice(0, 10),
        lastUpdatedBy: "Current User",
      };
    }));
    setEditingTemplate(null);
    toast.success(`Template saved as v${newVersion}`);
  };

  const handleRollback = (t: DocumentTemplate, version: number) => {
    const ver = t.versions.find(v => v.version === version);
    if (!ver) return;
    const newVersion = t.currentVersion + 1;
    const rollbackEntry: TemplateVersion = {
      version: newVersion,
      sections: ver.sections.map(s => ({ ...s })),
      createdAt: new Date().toISOString().slice(0, 10),
      createdBy: "Current User",
      changeNotes: `Rolled back to v${version}`,
    };
    setTemplates(prev => prev.map(tp => {
      if (tp.id !== t.id) return tp;
      return {
        ...tp,
        currentVersion: newVersion,
        versions: [...tp.versions, rollbackEntry],
        lastUpdatedAt: new Date().toISOString().slice(0, 10),
        lastUpdatedBy: "Current User",
      };
    }));
    setVersionDialog(null);
    toast.success(`Rolled back to v${version} (saved as v${newVersion})`);
  };

  const updateSection = (id: string, updates: Partial<TemplateSection>) => {
    setEditorSections(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const addSection = () => {
    const newSection: TemplateSection = {
      id: `s-${Date.now()}`,
      title: "New Section",
      type: "body",
      content: "",
      order: editorSections.length + 1,
    };
    setEditorSections(prev => [...prev, newSection]);
    setEditingSectionId(newSection.id);
  };

  const removeSection = (id: string) => {
    setEditorSections(prev => prev.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i + 1 })));
    if (editingSectionId === id) setEditingSectionId(null);
  };

  const insertVariable = (key: string) => {
    const tag = `{{${key}}}`;
    navigator.clipboard?.writeText(tag);
    toast.info(`Copied ${tag} — paste into your content`);
  };

  const exampleValues = getExampleValues();

  /* ── Editor View ── */
  if (editingTemplate) {
    const usedVars = extractVariables(editorSections.map(s => s.content).join("\n"));
    const validation = validateTemplate(editorSections);

    return (
      <div className="space-y-4">
        {/* Editor Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setEditingTemplate(null)}>
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
            <div>
              <h2 className="text-sm font-bold">{editingTemplate.name}</h2>
              <p className="text-[10px] text-muted-foreground">
                {editingTemplate.category} · v{editingTemplate.currentVersion} · Editing new version
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!validation.valid && (
              <Badge variant="outline" className="text-[9px] gap-1 bg-warning/10 text-warning border-warning/20">
                <AlertTriangle className="h-3 w-3" /> {validation.issues.length} issue{validation.issues.length > 1 ? "s" : ""}
              </Badge>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditingTemplate(null)}>Cancel</Button>
            <Button size="sm" className="h-8 text-xs gap-1.5" onClick={handleSaveTemplate}>
              <Check className="h-3.5 w-3.5" /> Save New Version
            </Button>
          </div>
        </div>

        <Tabs value={editorTab} onValueChange={(v) => setEditorTab(v as any)}>
          <TabsList className="bg-muted/60 h-8">
            <TabsTrigger value="edit" className="text-xs gap-1.5"><Pencil className="h-3 w-3" /> Edit</TabsTrigger>
            <TabsTrigger value="preview" className="text-xs gap-1.5"><Eye className="h-3 w-3" /> Preview</TabsTrigger>
            <TabsTrigger value="variables" className="text-xs gap-1.5"><Variable className="h-3 w-3" /> Variables</TabsTrigger>
            <TabsTrigger value="history" className="text-xs gap-1.5"><History className="h-3 w-3" /> History</TabsTrigger>
          </TabsList>

          {/* EDIT TAB */}
          <TabsContent value="edit" className="space-y-3 mt-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Version Notes</Label>
              <Input
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                placeholder="Describe what changed..."
                className="h-8 text-xs"
              />
            </div>

            {editorSections.sort((a, b) => a.order - b.order).map((section) => (
              <Card key={section.id} className={`border ${editingSectionId === section.id ? "border-primary/50 ring-1 ring-primary/20" : ""}`}>
                <CardHeader className="py-2.5 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40" />
                      {editingSectionId === section.id ? (
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(section.id, { title: e.target.value })}
                          className="h-7 text-xs font-semibold max-w-[200px]"
                        />
                      ) : (
                        <button onClick={() => setEditingSectionId(section.id)} className="text-xs font-semibold text-left hover:text-primary transition-colors">
                          {section.title}
                        </button>
                      )}
                      <Badge variant="outline" className="text-[9px] px-1.5">{section.type}</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingSectionId(editingSectionId === section.id ? null : section.id)}>
                        {editingSectionId === section.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </Button>
                      {editorSections.length > 1 && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => removeSection(section.id)}>
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {editingSectionId === section.id && (
                  <CardContent className="pt-0 px-4 pb-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] w-12">Type</Label>
                      <Select value={section.type} onValueChange={(v) => updateSection(section.id, { type: v as TemplateSection["type"] })}>
                        <SelectTrigger className="h-7 text-xs w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="header">Header</SelectItem>
                          <SelectItem value="body">Body</SelectItem>
                          <SelectItem value="terms">Terms</SelectItem>
                          <SelectItem value="signature">Signature</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Textarea
                      value={section.content}
                      onChange={(e) => updateSection(section.id, { content: e.target.value })}
                      className="text-xs font-mono min-h-[120px] leading-relaxed"
                      placeholder="Enter content... use {{variable_name}} for dynamic data"
                    />
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] text-muted-foreground mr-1">Insert:</span>
                      {TEMPLATE_VARIABLES.slice(0, 8).map(v => (
                        <button
                          key={v.key}
                          onClick={() => {
                            updateSection(section.id, { content: section.content + `{{${v.key}}}` });
                          }}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          {v.label}
                        </button>
                      ))}
                      <button
                        onClick={() => setEditorTab("variables")}
                        className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:bg-muted/80"
                      >
                        More...
                      </button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}

            <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs h-8 border-dashed" onClick={addSection}>
              <Plus className="h-3.5 w-3.5" /> Add Section
            </Button>

            {!validation.valid && (
              <Card className="border-warning/30 bg-warning/5">
                <CardContent className="py-3 px-4">
                  <p className="text-xs font-semibold text-warning flex items-center gap-1.5 mb-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Validation Issues
                  </p>
                  {validation.issues.map((issue, i) => (
                    <p key={i} className="text-[10px] text-muted-foreground">• {issue}</p>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* PREVIEW TAB */}
          <TabsContent value="preview" className="mt-3">
            <Card>
              <CardContent className="py-6 px-8">
                <div className="max-w-2xl mx-auto space-y-6">
                  {editorSections.sort((a, b) => a.order - b.order).map((section) => {
                    const rendered = resolveVariables(section.content, exampleValues);
                    return (
                      <div key={section.id}>
                        {section.type === "header" ? (
                          <div className="text-center border-b pb-4 mb-4">
                            <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{rendered}</pre>
                          </div>
                        ) : section.type === "signature" ? (
                          <div className="border-t pt-4 mt-6">
                            <p className="text-[10px] font-semibold text-muted-foreground mb-2 uppercase tracking-wider">{section.title}</p>
                            <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{rendered}</pre>
                          </div>
                        ) : section.type === "terms" ? (
                          <div className="bg-muted/30 rounded-lg p-4">
                            <p className="text-[10px] font-semibold text-muted-foreground mb-2 uppercase tracking-wider">{section.title}</p>
                            <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{rendered}</pre>
                          </div>
                        ) : (
                          <div>
                            <p className="text-[10px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">{section.title}</p>
                            <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{rendered}</pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 pt-4 border-t">
                  <p className="text-[10px] text-muted-foreground text-center">
                    Preview uses example data · {usedVars.length} variables resolved
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VARIABLES TAB */}
          <TabsContent value="variables" className="mt-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs flex items-center gap-1.5"><Variable className="h-3.5 w-3.5" /> Available Variables</CardTitle>
                <CardDescription className="text-[10px]">Click to copy a variable tag, then paste into template content</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(["customer", "quote", "financial", "system", "job"] as const).map(source => {
                    const vars = TEMPLATE_VARIABLES.filter(v => v.source === source);
                    if (vars.length === 0) return null;
                    return (
                      <div key={source}>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{source}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {vars.map(v => {
                            const isUsed = usedVars.includes(v.key);
                            return (
                              <button
                                key={v.key}
                                onClick={() => insertVariable(v.key)}
                                className={`flex items-center justify-between p-2 rounded-lg border text-left hover:bg-muted/50 transition-colors ${isUsed ? "border-primary/30 bg-primary/5" : ""}`}
                              >
                                <div>
                                  <p className="text-[10px] font-semibold">{v.label}</p>
                                  <code className="text-[9px] text-primary">{`{{${v.key}}}`}</code>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[9px] text-muted-foreground max-w-[100px] truncate">{v.example}</span>
                                  {isUsed && <Check className="h-3 w-3 text-primary" />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HISTORY TAB */}
          <TabsContent value="history" className="mt-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Version History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-0 divide-y">
                  {[...editingTemplate.versions].reverse().map(ver => (
                    <div key={ver.version} className="flex items-center justify-between py-2.5">
                      <div className="flex items-center gap-3">
                        <Badge variant={ver.version === editingTemplate.currentVersion ? "default" : "outline"} className="text-[9px] w-10 justify-center">
                          v{ver.version}
                        </Badge>
                        <div>
                          <p className="text-xs">{ver.changeNotes}</p>
                          <p className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                            <User className="h-3 w-3" /> {ver.createdBy} · <Clock className="h-3 w-3" /> {ver.createdAt}
                          </p>
                        </div>
                      </div>
                      {ver.version !== editingTemplate.currentVersion && (
                        <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={() => {
                          setEditorSections(ver.sections.map(s => ({ ...s })));
                          toast.info(`Loaded v${ver.version} into editor — save to create new version`);
                        }}>
                          <RotateCcw className="h-3 w-3" /> Load
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  /* ── Template List View ── */
  return (
    <div className="space-y-4">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Templates", value: stats.total, color: "text-primary" },
          { label: "Active", value: stats.active, color: "text-success" },
          { label: "Contracts", value: stats.contracts, color: "text-info" },
          { label: "Forms", value: stats.forms, color: "text-warning" },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="py-3 px-4">
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Documents & Forms</CardTitle>
              <CardDescription className="text-xs">Manage contracts, disclosures, change orders, and operational forms</CardDescription>
            </div>
            <Button size="sm" className="text-xs h-8 gap-1.5" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates..." className="pl-8 text-xs h-8" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="h-8 text-xs w-[130px]"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Contract">Contracts</SelectItem>
                <SelectItem value="Disclosure">Disclosures</SelectItem>
                <SelectItem value="Change Order">Change Orders</SelectItem>
                <SelectItem value="Form">Forms</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 text-xs w-[110px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
                <SelectItem value="Archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Template List */}
          <div className="space-y-2">
            {filtered.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors group">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => openEditor(t)} className="text-xs font-semibold hover:text-primary transition-colors text-left truncate">
                        {t.name}
                      </button>
                      <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${CATEGORY_STYLES[t.category]}`}>{t.category}</Badge>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      v{t.currentVersion} · Updated {t.lastUpdatedAt} by {t.lastUpdatedBy} · {getCurrentSections(t).length} sections
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={`text-[9px] ${STATUS_COLORS[t.status]}`}>{t.status}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="text-xs gap-2" onClick={() => openEditor(t)}>
                        <Pencil className="h-3 w-3" /> Edit Template
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs gap-2" onClick={() => {
                        setEditingTemplate(t);
                        setEditorSections(getCurrentSections(t));
                        setEditorTab("preview");
                      }}>
                        <Eye className="h-3 w-3" /> Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs gap-2" onClick={() => handleDuplicate(t)}>
                        <Copy className="h-3 w-3" /> Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs gap-2" onClick={() => setVersionDialog(t)}>
                        <History className="h-3 w-3" /> Version History
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-xs gap-2" onClick={() => handleToggleStatus(t.id)}>
                        {t.status === "Active" ? <><ToggleRight className="h-3 w-3" /> Deactivate</> : <><ToggleLeft className="h-3 w-3" /> Activate</>}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-xs gap-2" onClick={() => handleArchive(t.id)}>
                        <Archive className="h-3 w-3" /> Archive
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-xs gap-2 text-destructive" onClick={() => handleDelete(t.id)}>
                        <Trash2 className="h-3 w-3" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No templates found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Create Template</DialogTitle>
            <DialogDescription className="text-xs">Create a new document or form template</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Template Name</Label>
              <Input value={createDraft.name} onChange={(e) => setCreateDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g., Standard Contract v4" className="h-8 text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={createDraft.category} onValueChange={(v) => setCreateDraft(d => ({ ...d, category: v as TemplateCategory }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Contract">Contract</SelectItem>
                  <SelectItem value="Disclosure">Disclosure</SelectItem>
                  <SelectItem value="Change Order">Change Order</SelectItem>
                  <SelectItem value="Form">Form</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button size="sm" className="gap-1.5" disabled={!createDraft.name.trim()} onClick={handleCreate}>
              <Plus className="h-3.5 w-3.5" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={!!versionDialog} onOpenChange={(open) => !open && setVersionDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">Version History — {versionDialog?.name}</DialogTitle>
            <DialogDescription className="text-xs">View and rollback to previous versions</DialogDescription>
          </DialogHeader>
          {versionDialog && (
            <div className="space-y-0 divide-y max-h-[400px] overflow-y-auto">
              {[...versionDialog.versions].reverse().map(ver => (
                <div key={ver.version} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <Badge variant={ver.version === versionDialog.currentVersion ? "default" : "outline"} className="text-[9px] w-10 justify-center">
                      v{ver.version}
                    </Badge>
                    <div>
                      <p className="text-xs font-medium">{ver.changeNotes}</p>
                      <p className="text-[10px] text-muted-foreground">{ver.createdBy} · {ver.createdAt}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {ver.version === versionDialog.currentVersion ? (
                      <Badge className="text-[9px] bg-success/10 text-success border-success/20">Current</Badge>
                    ) : (
                      <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => handleRollback(versionDialog, ver.version)}>
                        <RotateCcw className="h-3 w-3" /> Rollback
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
